// src/lib/anlagenRegeln.js
// --- VORSCHLAEGE NACH ANLAGENART ----------------------------------------------
//
// Anna-Lena 13.09.2026: "wenn ich eine SF6-Anlage ohne Messfeld habe, brauche
// ich dort keine Erdungs- und Kurzschliessvorrichtung fuer die MS-Anlage; wenn
// ein Messfeld da ist, brauche ich eine; wenn der Trafo nicht gekapselt ist,
// auch fuer den Trafo MS- und NS-Seite; bei luftisolierten Anlagen braucht es
// mehr - das koennte man automatisieren."
//
// Grundsaetze (Recherche 13.09.2026, Quellen siehe regeln-recherche.md):
//   - Die App SCHLAEGT VOR, sie blendet nichts aus. Sicherheitsausruestung
//     bestaetigt immer der Monteur (ein Tipp uebernimmt den Vorschlag).
//   - Nur Regeln mit Sicherheit hoch/mittel bekommen den Ein-Tipp-Vorschlag
//     "nicht erforderlich". Unsichere Faelle erscheinen nur als Hinweis.
//   - Fehlt ein Stammdaten-Wert (Isolierart leer, keine MS-Felder erfasst,
//     Anschlussart unbekannt), feuert die Regel NICHT.
// Entscheidungen des Bueros (Anna-Lena 15.09.2026):
//   - 05.11 Erdungs- und Kurzschliessvorrichtung bleibt EIN Punkt (nicht MS/NS geteilt).
//   - SF6/Clean Air mit offenen Trafo-Anschluessen: MS-Spannungspruefer ERFORDERLICH.
//   - SF6/Clean Air: KEINE Schaltstange als Rettungsstange noetig.
//   - PSA (05.1-05.7) wird auch OHNE NSHV gebraucht - kein Hinweis mehr.
// Quellen u. a.: ETB-Broschuere 15, VDN-Richtlinie Transformatorstationen,
// DGUV Information 203-001, Netze BW TAB MS, Siemens-Katalog 8DJH.

// Felder mit Leistungsschalter: Abgang L, Kuppelfeld V, Erzeugungsanlage G und das
// Uebergabefeld UE (gibt es mit Leistungsschalter oder HH-Sicherung).
export const LS_FELDER = ['L', 'V', 'G', 'UE'];

// Kopf-Fragen im Abschluss, die zur Anlage passen muessen (Anna-Lena 18.09.2026):
// ohne Leistungsschalter keine Schutzpruefung, nur Giessharz-Trafos keine Oelprobe.
// Unbekannt (nichts geladen / keine Felder erfasst) = Frage zeigen.
export function kopfFragenLage(config, msFelder, trafos) {
  const felder = Array.isArray(msFelder) ? msFelder.filter(f => f.active !== false) : null;
  const tr = Array.isArray(trafos) ? trafos.filter(t => t.active !== false) : null;
  let hatLs = true;
  if (config?.ms_anlage_vorhanden === false) hatLs = false;
  else if (felder && felder.length > 0) hatLs = felder.some(f => LS_FELDER.includes(f.typ_code));
  const nurGh = !!tr && tr.length > 0 && tr.every(t => t.trafoart === 'giessharz');
  return { hatLs, nurGh };
}

const OS_OFFEN = ['kerzen', 'anschlussfahnen'];
// US-Werte seit Migration 48 (18.09.2026), alte Werte bleiben mitgezaehlt.
// Seit Migration 53 zusaetzlich mit / ohne Erdungsbolzen (_eb / _ohne_eb).
const US_OFFEN = ['kerzen', 'kerzen_flach', 'kerzen_fahne', 'klemmen', 'anschlussfahnen', 'anschlussschienen',
  'kerzen_flach_eb', 'kerzen_flach_ohne_eb', 'kerzen_fahne_eb', 'kerzen_fahne_ohne_eb', 'klemmen_eb', 'klemmen_ohne_eb'];
const US_ABGEDECKT = ['klemmen_hauben_kugel', 'klemmen_hauben_ohne_kugel', 'klemmen_und_hauben', 'anschlussschienen_abgedeckt'];

// Lage der Station aus den Stammdaten - einmal je Kapitel berechnen.
export function anlagenLage(config, msFelder, trafos) {
  const iso = config?.ms_anlage_isolierart || null;
  const felder = (msFelder || []).filter(f => f.active !== false);
  const tr = (trafos || []).filter(t => t.active !== false);
  const hat = (code) => felder.some(f => f.typ_code === code);
  const osBekannt = tr.length > 0 && tr.every(t => t.os_anschluss);
  const usBekannt = tr.length > 0 && tr.every(t => t.us_anschluss);
  return {
    GIS: iso === 'sf6' || iso === 'clean_air',
    AIS: iso === 'luft' || iso === 'luft_offen',
    LUFT_OFFEN: iso === 'luft_offen',
    ISO_BEKANNT: !!iso,
    MS_DA: config?.ms_anlage_vorhanden === true,
    KEINE_MS: config?.ms_anlage_vorhanden === false,
    KEINE_NSHV: config?.nshv_vorhanden === false,
    FELDER_BEKANNT: felder.length > 0,
    HAT_M: hat('M'), HAT_T: hat('T'),
    // Leistungsschalter sicher: L-Feld, Kuppelfeld V, Erzeugungsanlage G.
    // Das Uebergabefeld gibt es mit Leistungsschalter ODER mit HH-Sicherungen
    // (TB3310 Stromnetz Berlin) - deshalb zaehlt es fuer beides als "moeglich".
    LS_MOEGLICH: ['L', 'V', 'G', 'UE'].some(hat),
    KEINE_SICHERUNGSFELDER: !['T', 'UE'].some(hat),
    TRAFOS_BEKANNT: tr.length > 0,
    OS_OFFEN: tr.some(t => OS_OFFEN.includes(t.os_anschluss)),
    US_OFFEN: tr.some(t => US_OFFEN.includes(t.us_anschluss)),
    ALLE_OS_STECKER: osBekannt && tr.every(t => t.os_anschluss === 'stecker'),
    ALLE_US_ABGEDECKT: usBekannt && tr.every(t => US_ABGEDECKT.includes(t.us_anschluss)),
    ALLE_GH: tr.length > 0 && tr.every(t => t.trafoart === 'giessharz'),
    TYP: config?.stations_typ || null,
    OELWANNE_FEHLT: config?.oelauffangwanne_vorhanden === false,
    MS_MESSUNG: config?.verrechnungsmessung === 'ms',
    ERDER_FEHLT: config?.erderanlage_vorhanden === false,
    // 05.18/05.19 entfallen (Anna-Lena 18.09.2026): gasisoliert, Felder bekannt,
    // kein Messfeld, alle Trafos OS-seitig gesteckt. Nur wenn das SICHER ist.
    MS_PRUEFER_ENTFAELLT: (iso === 'sf6' || iso === 'clean_air') && felder.length > 0 && !hat('M')
      && tr.length > 0 && tr.every(t => t.os_anschluss === 'stecker'),
  };
}

const nichtNoetig = (text) => ({ art: 'nicht_erforderlich', text });
const noetig = (text) => ({ art: 'erforderlich', text });
const hinweis = (text) => ({ art: 'hinweis', text });

// Regel fuer EINEN Pruefpunkt. trafo: bei Kapitel 04 der Trafo dieses Kapitels.
// Ergebnis: { art: 'nicht_erforderlich' | 'erforderlich' | 'hinweis', text } oder null.
export function regelFuer(modulCode, pos, L, trafo) {
  const p = String(pos || '').trim();
  if (!L) return null;

  if (modulCode === '05-SICHERHEITS-ZUBEHOER') {
    const eukNoetig = L.AIS || L.HAT_M || L.OS_OFFEN || L.US_OFFEN;
    if (p === '05.11') {
      if (eukNoetig) {
        return noetig(L.AIS ? 'Luftisolierte Anlage: geerdet wird mit der tragbaren Erdungs- und Kurzschließvorrichtung.'
          : L.HAT_M ? 'Messfeld vorhanden: Messfelder haben meist keinen Erdungsschalter, sondern Kugelfestpunkte.'
          : 'Trafo mit offenen Anschlüssen: Er muss OS- und US-seitig direkt am Trafo geerdet werden.');
      }
      if (L.GIS && L.FELDER_BEKANNT && !L.HAT_M && L.ALLE_OS_STECKER && L.ALLE_US_ABGEDECKT) {
        return hinweis('MS-seitig voraussichtlich nicht nötig (Erdungsschalter in allen Feldern, Trafo gesteckt). NS-seitige Erdung trotzdem klären – bitte selbst bewerten.');
      }
      return null;
    }
    if (p === '05.10' || p === '05.12') {
      if (eukNoetig) return noetig('Gehört zur Erdungs- und Kurzschließvorrichtung, die hier nötig ist.');
      if (L.GIS && L.FELDER_BEKANNT && !L.HAT_M && L.ALLE_OS_STECKER) {
        return nichtNoetig('SF6/Clean-Air ohne Messfeld, Trafo gesteckt: Es gibt keine MS-Festpunkte zum Anlegen.');
      }
      return null;
    }
    if (p === '05.22') {
      if (L.ALLE_OS_STECKER) return nichtNoetig('Trafo OS-seitig gesteckt (Außenkonus): geerdet wird im Trafofeld der Schaltanlage.');
      if (L.OS_OFFEN) return noetig('Offene OS-Anschlüsse: Festpunkte zum Erden direkt am Trafo nötig.');
      return null;
    }
    if (p === '05.23') {
      if (L.US_OFFEN) return noetig('Offene US-Anschlüsse: Die US-Seite muss am Trafo geerdet werden können.');
      if (L.ALLE_US_ABGEDECKT) return hinweis('US abgedeckt: Festpunkte ggf. unter der Abdeckung oder an der NSHV – bitte prüfen.');
      return null;
    }
    if (p === '05.18' || p === '05.19') {
      if (L.AIS) return noetig('Luftisolierte Anlage: Spannungsfreiheit wird mit dem MS-Spannungsprüfer festgestellt.');
      if (L.GIS && L.OS_OFFEN) return noetig('SF6/Clean Air mit offenen Trafo-Anschlüssen: Spannungsfreiheit an den Anschlüssen mit dem MS-Spannungsprüfer feststellen.');
      if (L.GIS && L.HAT_M) return hinweis('Voraussichtlich nötig: am Messfeld prüfen, ob eigene Prüfbuchsen vorhanden sind.');
      if (L.GIS && L.FELDER_BEKANNT && L.TRAFOS_BEKANNT) return hinweis('Nicht nötig, wenn alle Felder kapazitive Spannungsprüfsysteme haben – dann 05.24 prüfen.');
      return null;
    }
    if (p === '05.24') {
      if (L.GIS) return noetig('SF6/Clean Air: kapazitive Prüfbuchsen – steckbares Anzeigegerät nötig (bei integrierter Anzeige wie CAPDIS/WEGA nicht). HR- und LRM-Geräte sind nicht tauschbar.');
      if (L.AIS && L.FELDER_BEKANNT && !L.HAT_M) return hinweis('Nur nötig, wenn die Felder kapazitive Prüfbuchsen haben (ältere Luftanlagen meist nicht).');
      return null;
    }
    if (p === '05.13' || p === '05.15') {
      if (L.AIS && L.HAT_T) return noetig('Luftisolierte Anlage mit Trafofeld: HH-Sicherungen nur mit der Sicherungszange wechseln.');
      if (L.GIS) return nichtNoetig('SF6/Clean Air: Sicherungen sitzen in Kammern, die nur geerdet zugänglich sind – Wechsel ohne Zange.');
      if (L.MS_DA && L.FELDER_BEKANNT && L.KEINE_SICHERUNGSFELDER) return nichtNoetig('Kein Trafofeld mit HH-Sicherungen erfasst.');
      return null;
    }
    if (p === '05.14' || p === '05.16' || p === '05.17') {
      if (L.HAT_T) return noetig('Trafofeld mit HH-Sicherungen: Sicherungen und Reserve (je Bemessungsstrom) gehören in die Station.');
      if (L.MS_DA && L.FELDER_BEKANNT && L.KEINE_SICHERUNGSFELDER) return nichtNoetig('Kein Trafofeld mit HH-Sicherungen erfasst.');
      return null;
    }
    if (p === '05.29') {
      if (L.AIS) return noetig('Luftisolierte Anlage: Schutzplatten werden in offene Trennstrecken geschoben.');
      if (L.GIS && L.FELDER_BEKANNT && !L.HAT_M) return nichtNoetig('Gekapselte Anlage ohne Messfeld: keine offenen Trennstrecken.');
      if (L.GIS && L.HAT_M) return hinweis('Luftisolierte Messfelder können Schutzplatten brauchen – je nach Bauart prüfen.');
      return null;
    }
    if (p === '05.20' || p === '05.21') {
      if (L.LUFT_OFFEN) return noetig('Offene Bauweise: Trenner werden mit der Schaltstange betätigt.');
      if (L.GIS) return nichtNoetig('SF6/Clean Air: geschaltet wird mit dem Schalthebel – eine Schaltstange ist nicht erforderlich.');
      return null;
    }
    if (p === '05.25' && L.KEINE_MS) return nichtNoetig('Keine eigene MS-Anlage.');
    if (p === '05.26' && L.KEINE_NSHV) return hinweis('Nur nötig, wenn eine NS-Anlage vorhanden ist.');
    if (p === '05.28' && L.TYP === 'kompakt') return hinweis('Bei Bedienung von außen (kein begehbarer Raum) evtl. nicht nötig.');
    return null;
  }

  if (modulCode === '02-MS-ANLAGE') {
    if (L.KEINE_MS) return nichtNoetig('Laut Stammdaten keine eigene MS-Anlage.');
    if (p === '02.1') {
      if (L.AIS) return nichtNoetig('Luftisolierte Anlage: kein Isoliergas, keine Gasdruckanzeige.');
      if (L.GIS) return noetig('SF6/Clean Air: Gasdruck- bzw. Bereitschaftsanzeige ablesen.');
    }
    if (p === '02.10' && L.FELDER_BEKANNT && L.KEINE_SICHERUNGSFELDER) return nichtNoetig('Kein Trafofeld mit HH-Sicherungen erfasst – keine Schlagstiftauslösung.');
    if (p === '02.11' && L.KEINE_NSHV) return hinweis('Nur relevant bei NS-Leistungsschalter am Trafo.');
    if (p === '02.8' && L.FELDER_BEKANNT && !L.LS_MOEGLICH) return hinweis('UMZ-/Differentialschutz meist nur bei Leistungsschalterfeld – Kurzschlussanzeiger trotzdem prüfen.');
    // 02.3 Isolierstoffe steckt seit Migration 52 in 02.2.
    if ((p === '02.2' || p === '02.3') && L.GIS) return hinweis('Isolierung liegt im Gasbehälter – nur sichtbare Teile (Kabelstecker, Durchführungen) beurteilen.');
    return null;
  }

  if (modulCode === '03-NSHV') {
    if (L.KEINE_NSHV) return nichtNoetig('Laut Stammdaten keine NS-Hauptverteilung in der Station.');
    return null;
  }

  if (modulCode === '01-STATIONSKOERPER') {
    if (L.TYP === 'im_gebaeude' && ['01.2.1', '01.2.2', '01.2.3'].includes(p)) return nichtNoetig('Station im Gebäude: kein eigenes Dach.');
    if (L.TYP === 'im_gebaeude' && p === '01.3.5') return nichtNoetig('Station im Gebäude: Türfeststeller nur für Außentüren gefordert.');
    if (L.TYP === 'im_gebaeude' && p === '01.3.7') return nichtNoetig('Station im Gebäude: kein Regen an den Schlössern.');
    if (L.TYP === 'kompakt' && p === '01.3.3') return hinweis('Bei nicht begehbarer Station (Bedienung von außen) evtl. nicht nötig.');
    // 01.4.5: KEIN Hinweis bei Kompaktstationen - der Boden im MS-Raum muss auch dort
    // verschraubt sein (Anna-Lena 22.09.2026).
    if (L.MS_MESSUNG && p === '01.3.6') return hinweis('Übergabestation mit MS-Messung: Doppelschließung mit dem VNB abstimmen.');
    return null;
  }

  if (modulCode === '04-TRAFO-SICHT' && trafo) {
    const os = trafo.os_anschluss, us = trafo.us_anschluss, art = trafo.trafoart;
    // 04.7 erscheint seit Migration 53 nur noch am Giessharz-Trafo, 04.2 nicht bei
    // Stecker - ein "n. benoetigt"-Vorschlag ist dort nicht mehr noetig.
    if (p === '04.7' && art === 'giessharz') return noetig('Gießharztrafo: Die Wicklungsoberfläche ist im Betrieb nicht berührungssicher.');
    if (p === '04.2') {
      if (OS_OFFEN.includes(os)) return noetig('Offene OS-Anschlüsse: Der Trafo muss OS-seitig direkt geerdet werden können.');
    }
    if (p === '04.3') {
      if (US_OFFEN.includes(us)) return noetig('Offene US-Anschlüsse: Der Trafo muss auch US-seitig geerdet werden können.');
      if (us === 'klemmen_hauben_kugel') return hinweis('US mit Hauben und Erdungsbolzen: Erdung am Trafo über die Erdungsbolzen – passende Erdungsgarnitur prüfen.');
      if (US_ABGEDECKT.includes(us)) return hinweis('US abgedeckt: Erdung an der nächsten NS-Schaltstelle – dort prüfen.');
    }
    if (['04.4', '04.5', '04.6'].includes(p)) {
      if (art === 'giessharz' || (os && os !== 'stecker') || US_OFFEN.includes(us)) {
        return noetig('Berührbare aktive Teile: Der Warnbalken verhindert das Hineingreifen.');
      }
      // 04.4/04.5/04.6 werden immer gebraucht (Anna-Lena 18.09. und 22.09.2026, Migration 69).
    }
    if (p === '04.1' && art) {
      return hinweis(art === 'oel_mit_ausdehner' ? 'Öl mit Ausdehner: Buchholzschutz und Thermometer prüfen.'
        : art === 'hermetic' ? 'Hermetik-Trafo: Hermetikschutz / Thermometer prüfen.'
        : 'Gießharztrafo: Kaltleiter und Auslösegerät prüfen.');
    }
    return null;
  }

  if (modulCode === '05-OEL-WANNE') {
    if (L.ALLE_GH) return nichtNoetig('Alle Trafos sind Gießharztrafos – kein Öl, nichts aufzufangen.');
    if (p === '05.1' && L.OELWANNE_FEHLT && L.TRAFOS_BEKANNT) {
      // Das Haekchen stand bis 20.09.2026 auf 'nein', weil es gar nicht einstellbar war.
      // Deshalb als Frage formuliert, nicht als Feststellung.
      return hinweis('In den Stammdaten ist keine Ölwanne eingetragen – bitte prüfen: bei einem Öltrafo wäre eine fehlende Wanne ein Mangel.');
    }
    return null;
  }

  if (modulCode === '07-ELEKTROTHERMOGRAFIE') {
    if (p === '07' && L.KEINE_NSHV) return nichtNoetig('Keine NS-Hauptverteilung in der Station.');
    if (p === '07.1' && L.KEINE_MS) return nichtNoetig('Keine eigene MS-Anlage.');
    if (p === '07.1' && L.GIS) return hinweis('Gasbehälter geschlossen – nur Kabelanschlussraum/Stecker messbar.');
    return null;
  }

  if (modulCode === '06-ERDERANLAGE' && L.ERDER_FEHLT) {
    return hinweis('Laut Stammdaten keine Erderanlage – jede Station braucht eine. Stammdaten prüfen.');
  }
  return null;
}

// Hinweise oben im Kapitel (Stammdaten-Plausibilitaet).
export function kapitelHinweise(modulCode, L) {
  if (!L) return [];
  const h = [];
  if (['05-SICHERHEITS-ZUBEHOER', '02-MS-ANLAGE'].includes(modulCode) && L.MS_DA && !L.ISO_BEKANNT) {
    h.push('Die Isolierart der MS-Anlage fehlt in den Stammdaten. Bitte eintragen – dann schlägt die App passende Punkte vor.');
  }
  if (modulCode === '02-MS-ANLAGE' && L.MS_MESSUNG && L.FELDER_BEKANNT && !L.HAT_M) {
    h.push('MS-Verrechnungsmessung, aber kein Messfeld erfasst? Stammdaten prüfen.');
  }
  if (modulCode === '05-SICHERHEITS-ZUBEHOER' && !L.FELDER_BEKANNT && L.MS_DA) {
    h.push('Keine MS-Felder erfasst – Vorschläge zu Sicherungen und Messfeld sind erst möglich, wenn die Felder eingetragen sind.');
  }
  return h;
}

// Welche Antwort-Option passt zu "nicht erforderlich"? (z. B. "n. benötigt",
// bei 05.22 "gekapselte Stecker"). null, wenn es keine gibt.
export function optionNichtErforderlich(pp) {
  const opts = Array.isArray(pp?.auswahl_optionen) ? pp.auswahl_optionen : [];
  // Nur Antworten, die auch als Antwortknopf erscheinen: "gekapselte Stecker"
  // allein ist ein Grund (Zusatz) - uebernommen sah der Punkt unbeantwortet aus.
  return opts.find(o => /gekapselt/i.test(o) && /^\s*i\.?\s?o/i.test(o))
    || opts.find(o => /ben(ö|oe)tigt/i.test(o))
    || null;
}
